import R from 'ramda';
import iso639_2 from 'iso-639/data/iso_639-2.json';
import * as applicationActions from 'application/applicationActions';
import * as dissertationInfoActions from './dissertationInfoActions';
import * as submissionActions from 'submission/submissionActions';

import {
  validateDissertationInfo,
  validateDissertationInfoField,
} from 'dissertation-info/dissertationValidation';

import {
  completeStep,
  getErrors,
  initialStepState,
  setCompleted,
  setErrors,
  setFieldValue,
  setFields,
  setLoadingInitData,
  setLoadingLocalStorage,
} from 'common/stepReducerUtil';

const initialDissertationInfoStepState = {
  categories: [],
  degrees: [],
  departments: [],
  licenseTypes: [],
  fields: {
    language: null,
    title: '',
    subtitle: '',
    doi: '',
    keywords: '',
    abstract: '',
    manuscriptCompletionYear: null,
    institutionName: '',
    degreeAwarded: null,
    department: null,
    primaryCategory: null,
    secondaryCategory: null,
    tertiaryCategory: null,
    licenseType: null,
    advisor: {
      firstName: '',
      lastName: '',
    },
    supervisor: {
      firstName: '',
      lastName: '',
    },
    committeeChair: {
      firstName: '',
      lastName: '',
    },
    committeeMembers: [
      { firstName: '', lastName: '' },
      { firstName: '', lastName: '' },
    ],
  },
};

const initialState = R.merge(
  initialStepState,
  initialDissertationInfoStepState,
);

const categoriesLens = R.lensProp('categories');
const setCategories = R.set(categoriesLens);
export const getCategories = R.view(categoriesLens);
const getCategoryForId = (categoryId, categories) =>
  R.find(R.propEq('categoryId', categoryId), categories);

const degreesLens = R.lensProp('degrees');
const setDegrees = R.set(degreesLens);
export const getDegrees = R.view(degreesLens);
const getDegreeForId = (degreeId, degrees) =>
  R.find(R.propEq('degreeId', degreeId), degrees);

const departmentsLens = R.lensProp('departments');
const setDepartments = R.set(departmentsLens);
export const getDepartments = R.view(departmentsLens);
const getDepartmentForId = (departmentId, departments) =>
  R.find(R.propEq('departmentId', departmentId), departments);

const committeeMembersLens = R.lensPath(['fields', 'committeeMembers']);
const addCommitteeMember = state =>
  R.over(
    committeeMembersLens,
    R.append({ firstName: '', lastName: '' }),
    state,
  );

const getLanguageForCode = (code, languages) =>
  R.find(R.propEq('code', code), languages);

export const mapIsoLanguages = R.memoize(
  R.pipe(
    R.values,
    R.uniqBy(R.prop('639-2')),
    R.sortBy(R.prop('en')),
    R.map(language => ({ code: language['639-2'], name: language.en[0] })),
    languages => {
      const eng = getLanguageForCode('eng', languages);
      const fra = getLanguageForCode('fra', languages);
      const ita = getLanguageForCode('ita', languages);
      const deu = getLanguageForCode('deu', languages);
      const spa = getLanguageForCode('spa', languages);

      return [
        eng,
        fra,
        ita,
        deu,
        spa,
        ...R.without([eng, fra, ita, deu, spa], languages),
      ];
    },
  ),
);

export const getLanguages = () => mapIsoLanguages(iso639_2);

const licenseTypesLens = R.lensProp('licenseTypes');
const setLicenseTypes = R.set(licenseTypesLens);
export const getLicenseTypes = R.view(licenseTypesLens);

const getLicenseTypeForId = (licenseTypeId, licenseTypes) =>
  R.find(R.propEq('licenseTypeId', licenseTypeId), licenseTypes);

const degreeChanged = (degreeId, state) =>
  R.pipe(
    setFieldValue(
      ['degreeAwarded'],
      getDegreeForId(degreeId, getDegrees(state)),
    ),
    setCompleted(false),
  )(state);

const departmentChanged = (departmentId, state) =>
  R.pipe(
    setFieldValue(
      ['department'],
      getDepartmentForId(departmentId, getDepartments(state)),
    ),
    setCompleted(false),
  )(state);

const fieldBlurred = (field, value, state) =>
  setErrors(
    validateDissertationInfoField(field, value, getErrors(state)),
    state,
  );

const fieldChanged = (path, value, state) =>
  R.pipe(setFieldValue(path, value), setCompleted(false))(state);

const initDataLoaded = (degrees, departments, licenseTypes, state) =>
  R.pipe(
    setDegrees(degrees),
    setDepartments(departments),
    setLicenseTypes(licenseTypes),
    setLoadingInitData(false),
  )(state);

const languageChanged = (code, state) =>
  R.pipe(
    setFieldValue(['language'], getLanguageForCode(code, getLanguages())),
    setCompleted(false),
  )(state);

const localStorageLoaded = (data, state) =>
  R.pipe(
    setCompleted(R.isEmpty(data) ? initialState.completed : data.completed),
    setFields(R.isEmpty(data) ? initialState.fields : data.fields),
    setLoadingLocalStorage(false),
  )(state);

const primaryCategoryChanged = (categoryId, state) =>
  R.pipe(
    setFieldValue(
      ['primaryCategory'],
      getCategoryForId(categoryId, getCategories(state)),
    ),
    setCompleted(false),
  )(state);

const secondaryCategoryChanged = (categoryId, state) =>
  R.pipe(
    setFieldValue(
      ['secondaryCategory'],
      getCategoryForId(categoryId, getCategories(state)),
    ),
    setCompleted(false),
  )(state);

const tertiaryCategoryChanged = (categoryId, state) =>
  R.pipe(
    setFieldValue(
      ['tertiaryCategory'],
      getCategoryForId(categoryId, getCategories(state)),
    ),
    setCompleted(false),
  )(state);

const licenseTypeChanged = (licenseTypeId, state) =>
  R.pipe(
    setCompleted(false),
    setFieldValue(
      ['licenseType'],
      getLicenseTypeForId(licenseTypeId, getLicenseTypes(state)),
    ),
  )(state);

const cancelSubmission = R.pipe(
  setCompleted(false),
  setErrors(initialState.errors),
  setFields(initialState.fields),
);

const appDataLoaded = (data, state) => {
  const categories = R.path(['project', 'categories'], data);
  return setCategories(categories || [], state);
};

const submissionReducer = (state = initialState, action) => {
  switch (action.type) {
    case dissertationInfoActions.ADD_COMMITTEE_MEMBER:
      return addCommitteeMember(state);

    case dissertationInfoActions.COMPLETE_STEP:
    case submissionActions.SUBMIT:
      return completeStep(validateDissertationInfo, state);

    case dissertationInfoActions.DEGREE_CHANGED:
      return degreeChanged(action.degreeId, state);

    case dissertationInfoActions.DEPARTMENT_CHANGED:
      return departmentChanged(action.departmentId, state);

    case dissertationInfoActions.FIELD_BLURRED:
      return fieldBlurred(action.field, action.value, state);

    case dissertationInfoActions.FIELD_CHANGED:
      return fieldChanged(action.path, action.value, state);

    case dissertationInfoActions.INIT_DATA_LOADED:
      return initDataLoaded(
        action.degrees,
        action.departments,
        action.licenseTypes,
        state,
      );

    case dissertationInfoActions.LANGUAGE_CHANGED:
      return languageChanged(action.code, state);

    case dissertationInfoActions.LICENSE_TYPE_CHANGED:
      return licenseTypeChanged(action.licenseTypeId, state);

    case dissertationInfoActions.LOCAL_STORAGE_LOADED:
      return localStorageLoaded(action.data, state);

    case dissertationInfoActions.PRIMARY_CATEGORY_CHANGED:
      return primaryCategoryChanged(action.categoryId, state);

    case dissertationInfoActions.SECONDARY_CATEGORY_CHANGED:
      return secondaryCategoryChanged(action.categoryId, state);

    case dissertationInfoActions.TERTIARY_CATEGORY_CHANGED:
      return tertiaryCategoryChanged(action.categoryId, state);

    case submissionActions.CANCEL_SUBMISSION:
      return cancelSubmission(state);

    case applicationActions.APP_DATA_LOADED:
      return appDataLoaded(action.data, state);

    default:
      return state;
  }
};

export default submissionReducer;



// WEBPACK FOOTER //
// ./src/dissertation-info/dissertationInfoReducer.js